
// | This is an experimental implementation of React hooks on [react-basic](https://github.com/lumihq/purescript-react-basic).
// |
// | *Warning:* This API is *experimental* and relies on alpha-release React versions.
// | It's here to allow experimentation while we get feedback on the API and wait for an official React release which supports hooks.
// | For more info on hooks, see [React's documentation](https://reactjs.org/docs/hooks-intro.html).
// |
// | It's also recommended while using this module to use PureScript's new "qualified do" syntax (it's used in the examples, `React.do`).
// | It's available in the  `0.12.2` release.
// |
// | If we prefer this API over the existing react-basic API, we may eventually replace it with this.
// |
// | *A note on Refs:* The `Ref` type is useful for passing to DOM nodes, but while this module remains a small extension to the existing react-basic library it won't be possible to pass a `ref` prop to the native DOM components.
// | In the meantime, use `element (unsafeCreateDOMComponent "div") { ref: elementRef }`.
"use strict";
var $foreign = require("./foreign.js");
var Control_Applicative = require("../Control.Applicative/index.js");
var Control_Applicative_Indexed = require("../Control.Applicative.Indexed/index.js");
var Control_Apply = require("../Control.Apply/index.js");
var Control_Apply_Indexed = require("../Control.Apply.Indexed/index.js");
var Control_Bind = require("../Control.Bind/index.js");
var Control_Bind_Indexed = require("../Control.Bind.Indexed/index.js");
var Data_Eq = require("../Data.Eq/index.js");
var Data_Function = require("../Data.Function/index.js");
var Data_Function_Uncurried = require("../Data.Function.Uncurried/index.js");
var Data_Functor = require("../Data.Functor/index.js");
var Data_Functor_Indexed = require("../Data.Functor.Indexed/index.js");
var Data_Maybe = require("../Data.Maybe/index.js");
var Data_Monoid = require("../Data.Monoid/index.js");
var Data_Newtype = require("../Data.Newtype/index.js");
var Data_Nullable = require("../Data.Nullable/index.js");
var Data_Semigroup = require("../Data.Semigroup/index.js");
var Data_Tuple = require("../Data.Tuple/index.js");
var Data_Tuple_Nested = require("../Data.Tuple.Nested/index.js");
var Effect = require("../Effect/index.js");
var Effect_Uncurried = require("../Effect.Uncurried/index.js");
var Prelude = require("../Prelude/index.js");
var React_Basic = require("../React.Basic/index.js");
var Type_Equality = require("../Type.Equality/index.js");
var Unsafe_Coerce = require("../Unsafe.Coerce/index.js");
var Unsafe_Reference = require("../Unsafe.Reference/index.js");
var UnsafeReference = function (x) {
    return x;
};

// | Render represents the effects allowed within a React component's
// | body, i.e. during "render". This includes hooks and ends with
// | returning JSX (see `pure`), but does not allow arbitrary side
// | effects.
var Render = function (x) {
    return x;
};
var writeRef = Effect_Uncurried.runEffectFn2($foreign.writeRef_);
var useState = function (initialState) {
    return function () {
        return $foreign.useState_(Data_Function_Uncurried.mkFn2(Data_Tuple.Tuple.create), initialState);
    };
};
var useRef = function (initialValue) {
    return function () {
        return $foreign.useRef_(initialValue);
    };
};
var useReducer = function (initialState) {
    return function (reducer) {
        return function () {
            return $foreign.useReducer_(Data_Function_Uncurried.mkFn2(Data_Tuple.Tuple.create), Data_Function_Uncurried.mkFn2(reducer), initialState);
        };
    };
};
var useMemoLazy = function (dictEq) {
    return function (key) {
        return function (computeA) {
            return function () {
                return $foreign.useMemoLazy_(Data_Function_Uncurried.mkFn2(Data_Eq.eq(dictEq)), key, computeA);
            };
        };
    };
};
var useMemo = function (dictEq) {
    return function (key) {
        return function (a) {
            return function () {
                return $foreign.useMemo_(Data_Function_Uncurried.mkFn2(Data_Eq.eq(dictEq)), a);
            };
        };
    };
};
var useLayoutEffect = function (dictEq) {
    return function (keys) {
        return function (effect) {
            return function () {
                return $foreign.useLayoutEffect_(Data_Function_Uncurried.mkFn2(Data_Eq.eq(dictEq)), keys, effect);
            };
        };
    };
};
var useEffect = function (dictEq) {
    return function (key) {
        return function (effect) {
            return function () {
                return $foreign.useEffect_(Data_Function_Uncurried.mkFn2(Data_Eq.eq(dictEq)), key, effect);
            };
        };
    };
};
var useContext = function (context) {
    return Data_Functor.map(Effect.functorEffect)(Data_Nullable.toMaybe)(function () {
        return $foreign.useContext_(context);
    });
};
var unsafeReactFunctionComponent = Unsafe_Coerce.unsafeCoerce;
var semigroupRender = function (dictTypeEquals) {
    return function (dictSemigroup) {
        return new Data_Semigroup.Semigroup(function (v) {
            return function (v1) {
                return Data_Semigroup.append(Effect.semigroupEffect(dictSemigroup))(v)(v1);
            };
        });
    };
};
var readRef = Effect_Uncurried.runEffectFn1($foreign.readRef_);
var readRefMaybe = function (a) {
    return Data_Functor.map(Effect.functorEffect)(Data_Nullable.toMaybe)(readRef(a));
};
var renderRefMaybe = function (a) {
    return readRefMaybe(a);
};
var renderRef = function (ref) {
    return readRef(ref);
};
var newtypeUnsafeReference = new Data_Newtype.Newtype(function (n) {
    return n;
}, UnsafeReference);
var monoidRender = function (dictTypeEquals) {
    return function (dictMonoid) {
        return new Data_Monoid.Monoid(function () {
            return semigroupRender(dictTypeEquals)(dictMonoid.Semigroup0());
        }, Data_Monoid.mempty(Effect.monoidEffect(dictMonoid)));
    };
};
var memo = Data_Function.flip(Control_Bind.bind(Effect.bindEffect))(Effect_Uncurried.runEffectFn1($foreign.memo_));
var ixFunctorRender = new Data_Functor_Indexed.IxFunctor(function (f) {
    return function (v) {
        return Data_Functor.map(Effect.functorEffect)(f)(v);
    };
});
var ixApplyRender = new Control_Apply_Indexed.IxApply(function () {
    return ixFunctorRender;
}, function (v) {
    return function (v1) {
        return Control_Apply.apply(Effect.applyEffect)(v)(v1);
    };
});
var ixBindRender = new Control_Bind_Indexed.IxBind(function () {
    return ixApplyRender;
}, function (v) {
    return function (f) {
        return function __do() {
            var a = v();
            var v1 = f(a);
            return v1();
        };
    };
});
var ixApplicativeRender = new Control_Applicative_Indexed.IxApplicative(function () {
    return ixApplyRender;
}, function (a) {
    return Control_Applicative.pure(Effect.applicativeEffect)(a);
});
var functorRender = new Data_Functor.Functor(function (f) {
    return function (v) {
        return Data_Functor.map(Effect.functorEffect)(f)(v);
    };
});
var eqUnsafeReference = new Data_Eq.Eq(Unsafe_Reference.unsafeRefEq);

// | Exported for use with qualified-do syntax
var discard = function (dictIxBind) {
    return Control_Bind_Indexed.ibind(dictIxBind);
};
var contextProvider = function (context) {
    return function (a) {
        return function (child) {
            return React_Basic.element($foreign.contextProvider_(context))({
                value: a,
                children: child
            });
        };
    };
};

// | Create a React component given a display name and render function.
// | Creating components is effectful because React uses the function
// | instance as the component's "identity" or "type". Components should
// | be created during a bootstrap phase and not within component
// | lifecycles or render functions.
var component = function (name) {
    return function (renderFn) {
        var c = unsafeReactFunctionComponent(function (props) {
            var v = renderFn(props);
            return v();
        });
        return function () {
            return $foreign.unsafeSetDisplayName(name, c);
        };
    };
};

// | Exported for use with qualified-do syntax
var bind = function (dictIxBind) {
    return Control_Bind_Indexed.ibind(dictIxBind);
};
var applyRender = function (dictTypeEquals) {
    return new Control_Apply.Apply(function () {
        return functorRender;
    }, function (v) {
        return function (v1) {
            return Control_Apply.apply(Effect.applyEffect)(v)(v1);
        };
    });
};
var bindRender = function (dictTypeEquals) {
    return new Control_Bind.Bind(function () {
        return applyRender(dictTypeEquals);
    }, function (v) {
        return function (f) {
            return function __do() {
                var a = v();
                var v1 = f(a);
                return v1();
            };
        };
    });
};
var applicativeRender = function (dictTypeEquals) {
    return new Control_Applicative.Applicative(function () {
        return applyRender(dictTypeEquals);
    }, function (a) {
        return Control_Applicative.pure(Effect.applicativeEffect)(a);
    });
};
module.exports = {
    component: component,
    memo: memo,
    useState: useState,
    useEffect: useEffect,
    useLayoutEffect: useLayoutEffect,
    useReducer: useReducer,
    readRef: readRef,
    readRefMaybe: readRefMaybe,
    writeRef: writeRef,
    renderRef: renderRef,
    renderRefMaybe: renderRefMaybe,
    useRef: useRef,
    useContext: useContext,
    contextProvider: contextProvider,
    useMemo: useMemo,
    useMemoLazy: useMemoLazy,
    UnsafeReference: UnsafeReference,
    bind: bind,
    discard: discard,
    newtypeUnsafeReference: newtypeUnsafeReference,
    eqUnsafeReference: eqUnsafeReference,
    ixFunctorRender: ixFunctorRender,
    ixApplyRender: ixApplyRender,
    ixApplicativeRender: ixApplicativeRender,
    ixBindRender: ixBindRender,
    functorRender: functorRender,
    applyRender: applyRender,
    applicativeRender: applicativeRender,
    bindRender: bindRender,
    semigroupRender: semigroupRender,
    monoidRender: monoidRender,
    createContext: $foreign.createContext,
    displayName: $foreign.displayName
};
